import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogIn, Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO_URL } from "@/lib/siteData";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password.");
      return;
    }
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      const to = location.state?.from || "/admin";
      navigate(to, { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen relative bg-white flex items-center justify-center px-5 overflow-hidden"
      data-testid="admin-login-page"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute -top-24 -right-20 w-[420px] h-[420px] rounded-full blur-3xl opacity-25 animate-blob"
          style={{ background: "radial-gradient(circle at 50% 50%, #6366f1, transparent 60%)" }}
        />
        <div
          className="absolute -bottom-24 -left-10 w-[360px] h-[360px] rounded-full blur-3xl opacity-25 animate-blob"
          style={{ background: "radial-gradient(circle at 50% 50%, #ec4899, transparent 60%)", animationDelay: "3s" }}
        />
      </div>

      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
        <div className="flex items-center gap-3 mb-8">
          <img src={LOGO_URL} alt="RPG Rasayan Udyog" className="h-12 w-auto" />
          <div className="flex flex-col leading-tight">
            <span className="font-display text-lg font-bold uppercase tracking-tight text-navy">RPG Rasayan</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Admin</span>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-6">
          <span className="w-10 h-10 rounded-lg gradient-accent-soft flex items-center justify-center text-indigo-600">
            <Lock size={18} />
          </span>
          <div>
            <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-slate-900">
              Admin Login
            </h1>
            <p className="text-sm text-slate-500">Manage incoming quote &amp; contact submissions.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label htmlFor="al-email" className="text-slate-900 font-semibold">Email</Label>
            <Input
              id="al-email"
              data-testid="admin-login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@rpgrasayan.com"
              autoComplete="username"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="al-pw" className="text-slate-900 font-semibold">Password</Label>
            <Input
              id="al-pw"
              data-testid="admin-login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <Button
            type="submit"
            data-testid="admin-login-submit"
            disabled={submitting}
            className="w-full gradient-accent text-white font-semibold uppercase tracking-wider rounded-md hover:opacity-90 transition-opacity h-12"
          >
            {submitting ? <Loader2 className="animate-spin mr-2" size={18} /> : <LogIn className="mr-2" size={18} />}
            {submitting ? "Signing in..." : "Sign In"}
          </Button>
        </form>

        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-slate-500 hover:text-slate-900"
          data-testid="admin-login-back-link"
        >
          <ArrowLeft size={14} /> Back to website
        </Link>
      </div>
    </div>
  );
}
